import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, DatabaseIcon, FilterIcon, BookOpenIcon, FileTextIcon, LinkIcon, TrendingUpIcon, SparklesIcon, LayoutGridIcon, ListIcon, ExternalLinkIcon, BuildingIcon } from 'lucide-react';
import { seedData } from '../data/seedData';
import { useUser } from '../lib/userContext';
interface SpacesExplorerProps {
  onSelectSpace: (spaceId: string) => void;
}
export function SpacesExplorer({
  onSelectSpace
}: SpacesExplorerProps) {
  const {
    hasPermission
  } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'external' | 'internal'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Extract all unique spaces from accounts with realistic enterprise-scale numbers
  const allSpaces = useMemo(() => {
    const spacesMap = new Map();
    seedData.accounts.forEach(account => {
      account.bots.forEach(bot => {
        // Filter by permissions
        if (bot.type === 'internal' && !hasPermission('view_internal_spaces')) {
          return;
        }
        bot.bound_spaces.forEach(space => {
          if (!spacesMap.has(space.id)) {
            // Generate realistic numbers for enterprise accounts
            const isInternal = bot.type === 'internal';
            const docCount = isInternal ? 450 + Math.floor(Math.random() * 800) // Internal: 450-1,250 docs
            : 180 + Math.floor(Math.random() * 420); // External: 180-600 docs
            const linkCount = isInternal ? 120 + Math.floor(Math.random() * 280) // Internal: 120-400 links
            : 45 + Math.floor(Math.random() * 155); // External: 45-200 links
            spacesMap.set(space.id, {
              ...space,
              botType: bot.type,
              doc_count: docCount,
              link_count: linkCount,
              accounts: [{
                id: account.id,
                name: account.name,
                logo: account.logo_url
              }]
            });
          } else {
            const existing = spacesMap.get(space.id);
            if (!existing.accounts.some((a: any) => a.id === account.id)) {
              existing.accounts.push({
                id: account.id,
                name: account.name,
                logo: account.logo_url
              });
            }
          }
        });
      });
    });
    return Array.from(spacesMap.values());
  }, [hasPermission]);
  // Filter spaces
  const filteredSpaces = useMemo(() => {
    let spaces = allSpaces;
    // Apply type filter
    if (filterType !== 'all') {
      spaces = spaces.filter(space => space.botType === filterType);
    }
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      spaces = spaces.filter(space => space.name.toLowerCase().includes(query) || space.accounts.some((acc: any) => acc.name.toLowerCase().includes(query)));
    }
    return spaces;
  }, [allSpaces, filterType, searchQuery]);
  // Calculate stats
  const stats = useMemo(() => {
    const total = allSpaces.length;
    const external = allSpaces.filter(s => s.botType === 'external').length;
    const internal = allSpaces.filter(s => s.botType === 'internal').length;
    const totalDocs = allSpaces.reduce((sum, s) => sum + (s.doc_count || 0), 0);
    const totalLinks = allSpaces.reduce((sum, s) => sum + (s.link_count || 0), 0);
    return {
      total,
      external,
      internal,
      totalDocs,
      totalLinks
    };
  }, [allSpaces]);
  return <div className="space-y-8" data-id="element-2510">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden" data-id="element-2511">
        <div className="absolute inset-0 -z-10" data-id="element-2512">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" data-id="element-2513" />
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" data-id="element-2514" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative" data-id="element-2515">
          <div className="flex items-center justify-between mb-6" data-id="element-2516">
            <div data-id="element-2517">
              <h1 className="text-4xl font-bold text-white mb-2 tracking-tight bg-gradient-to-r from-white via-purple-50 to-slate-200 bg-clip-text text-transparent" data-id="element-2518">
                Knowledge Spaces
              </h1>
              <p className="text-slate-400 text-lg" data-id="element-2519">
                Explore and manage Knowledge Spaces across your accounts
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-2520">
              <button onClick={() => setViewMode('grid')} className={`p-3 rounded-xl transition-all duration-200 ${viewMode === 'grid' ? 'bg-purple-600 text-white shadow-xl shadow-purple-600/40' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'}`} title="Grid View" data-id="element-2521">
                <LayoutGridIcon className="w-5 h-5" data-id="element-2522" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-3 rounded-xl transition-all duration-200 ${viewMode === 'list' ? 'bg-purple-600 text-white shadow-xl shadow-purple-600/40' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50'}`} title="List View" data-id="element-2523">
                <ListIcon className="w-5 h-5" data-id="element-2524" />
              </button>
            </div>
          </div>
          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-id="element-2525">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2526">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2527" />
              <div className="relative" data-id="element-2528">
                <div className="flex items-center justify-between mb-3" data-id="element-2529">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-2530">
                    <DatabaseIcon className="w-5 h-5 text-purple-400" data-id="element-2531" />
                  </div>
                  <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2532">
                    {stats.total}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-2533">
                  Total Spaces
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2534">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2535" />
              <div className="relative" data-id="element-2536">
                <div className="flex items-center justify-between mb-3" data-id="element-2537">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors" data-id="element-2538">
                    <DatabaseIcon className="w-5 h-5 text-blue-400" data-id="element-2539" />
                  </div>
                  <span className="text-3xl font-bold text-blue-400 group-hover:scale-110 transition-transform" data-id="element-2540">
                    {stats.external}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-2541">
                  External
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2542">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2543" />
              <div className="relative" data-id="element-2544">
                <div className="flex items-center justify-between mb-3" data-id="element-2545">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-2546">
                    <DatabaseIcon className="w-5 h-5 text-purple-400" data-id="element-2547" />
                  </div>
                  <span className="text-3xl font-bold text-purple-400 group-hover:scale-110 transition-transform" data-id="element-2548">
                    {stats.internal}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-2549">
                  Internal
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2550">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2551" />
              <div className="relative" data-id="element-2552">
                <div className="flex items-center justify-between mb-3" data-id="element-2553">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors" data-id="element-2554">
                    <FileTextIcon className="w-5 h-5 text-amber-400" data-id="element-2555" />
                  </div>
                  <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2556">
                    {stats.totalDocs}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-2557">
                  Documents
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-5 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-2558">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-2559" />
              <div className="relative" data-id="element-2560">
                <div className="flex items-center justify-between mb-3" data-id="element-2561">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors" data-id="element-2562">
                    <LinkIcon className="w-5 h-5 text-green-400" data-id="element-2563" />
                  </div>
                  <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-2564">
                    {stats.totalLinks}
                  </span>
                </div>
                <p className="text-xs font-semibold text-slate-400 group-hover:text-slate-300 transition-colors uppercase tracking-wider" data-id="element-2565">
                  Links
                </p>
              </div>
            </motion.div>
          </div>
          {/* Enhanced Filters */}
          <div className="flex flex-col md:flex-row gap-4 pb-2" data-id="element-2566">
            {/* Search */}
            <div className="flex-1 relative" data-id="element-2567">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2568" />
              <input type="text" placeholder="Search knowledge spaces..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-900/60 border border-slate-800/80 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500/40 transition-all duration-200 shadow-lg shadow-black/20" data-id="element-2569" />
            </div>
            {/* Type Filter */}
            <div className="flex gap-2 pb-1" data-id="element-2570">
              {(['all', 'external', 'internal'] as const).map(type => <button key={type} onClick={() => setFilterType(type)} className={`px-6 py-4 rounded-xl text-sm font-semibold transition-all duration-200 ${filterType === type ? type === 'all' ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white ring-2 ring-purple-500/40 scale-[1.02]' : type === 'external' ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white ring-2 ring-blue-500/40 scale-[1.02]' : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white ring-2 ring-purple-500/40 scale-[1.02]' : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 hover:scale-[1.02]'}`} data-id="element-2571">
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>)}
            </div>
          </div>
        </motion.div>
      </div>
      {/* Enhanced Spaces Grid/List */}
      {filteredSpaces.length === 0 ? <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="text-center py-20" data-id="element-2572">
          <div className="w-20 h-20 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-2573">
            <SearchIcon className="w-10 h-10 text-slate-500" data-id="element-2574" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-2575">
            No Knowledge Spaces found
          </h3>
          <p className="text-slate-400 mb-6" data-id="element-2576">
            Try adjusting your search or filters
          </p>
          <button onClick={() => {
        setSearchQuery('');
        setFilterType('all');
      }} className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all shadow-xl shadow-purple-600/40" data-id="element-2577">
            Clear Filters
          </button>
        </motion.div> : <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.2
    }} className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-id="element-2578">
          {filteredSpaces.map((space, index) => <motion.div key={space.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1 + index * 0.05
      }} onClick={() => onSelectSpace(space.id)} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl overflow-hidden hover:border-slate-700/80 hover:shadow-2xl hover:shadow-purple-500/10 transition-all duration-500 shadow-xl shadow-black/40 cursor-pointer" data-id="element-2579">
              {/* Decorative gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${space.botType === 'external' ? 'from-blue-500/5 via-transparent to-blue-500/5' : 'from-purple-500/5 via-transparent to-purple-500/5'} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} data-id="element-2580" />
              {/* Content */}
              <div className="relative p-6 space-y-4" data-id="element-2581">
                {/* Header */}
                <div className="flex items-start justify-between gap-4" data-id="element-2582">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-2583">
                    <div className={`w-12 h-12 rounded-xl ${space.botType === 'external' ? 'bg-gradient-to-br from-blue-600 to-blue-700' : 'bg-gradient-to-br from-purple-600 to-purple-700'} flex items-center justify-center shadow-lg ring-2 ${space.botType === 'external' ? 'ring-blue-500/20' : 'ring-purple-500/20'} group-hover:scale-110 transition-all duration-300 flex-shrink-0`} data-id="element-2584">
                      <DatabaseIcon className="w-6 h-6 text-white" data-id="element-2585" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2586">
                      <h3 className="text-lg font-bold text-white truncate group-hover:text-purple-400 transition-colors duration-300" data-id="element-2587">
                        {space.name}
                      </h3>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-xs font-bold border shadow-lg ${space.botType === 'external' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-purple-500/20 text-purple-400 border-purple-500/30'}`} data-id="element-2588">
                    {space.botType}
                  </span>
                </div>
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3" data-id="element-2589">
                  <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/60 hover:border-slate-700/60 transition-all" data-id="element-2590">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2591">
                      <FileTextIcon className="w-4 h-4 text-amber-400 flex-shrink-0" data-id="element-2592" />
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2593">
                        Documents
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-white" data-id="element-2594">
                      {space.doc_count?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-slate-950/60 rounded-xl p-3 border border-slate-800/60 hover:border-slate-700/60 transition-all" data-id="element-2595">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2596">
                      <LinkIcon className="w-4 h-4 text-green-400 flex-shrink-0" data-id="element-2597" />
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider" data-id="element-2598">
                        Links
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-white" data-id="element-2599">
                      {space.link_count?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
                {/* Accounts */}
                <div className="bg-slate-950/60 rounded-xl p-4 border border-slate-800/60" data-id="element-2600">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2601">
                    <BuildingIcon className="w-4 h-4 text-blue-400 flex-shrink-0" data-id="element-2602" />
                    <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider" data-id="element-2603">
                      Used by {space.accounts.length} account
                      {space.accounts.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2604">
                    {space.accounts.slice(0, 3).map((account: any) => <div key={account.id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/60 hover:bg-slate-700/60 rounded-lg border border-slate-700/40 hover:border-slate-600 transition-all text-xs text-slate-300 hover:text-white" data-id="element-2605">
                        <div className="w-5 h-5 rounded bg-white/95 p-0.5 flex items-center justify-center" data-id="element-2606">
                          <BuildingIcon className="w-3 h-3 text-slate-700" data-id="element-2607" />
                        </div>
                        <span className="font-medium truncate max-w-[120px]" data-id="element-2608">
                          {account.name}
                        </span>
                      </div>)}
                    {space.accounts.length > 3 && <div className="px-3 py-1.5 bg-slate-800/60 rounded-lg border border-slate-700/40 text-xs text-slate-400 font-medium" data-id="element-2609">
                        +{space.accounts.length - 3} more
                      </div>}
                  </div>
                </div>
                {/* View Button */}
                <button className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm font-bold rounded-xl transition-all duration-200 shadow-xl shadow-purple-600/30 hover:shadow-2xl hover:shadow-purple-600/40 flex items-center justify-center gap-2 hover:scale-[1.02]" data-id="element-2610">
                  <SparklesIcon className="w-4 h-4" data-id="element-2611" />
                  View Knowledge Space
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-2612" />
                </button>
              </div>
            </motion.div>)}
        </motion.div>}
    </div>;
}